import { createConfigParametersRepository } from '../../configParametersRepository';
import { shouldSkipTracking } from '../utils/cookie';
import { datalayerGlobal } from './datalayerMap';
import {
  DataLayerEventPayload,
  Datalayer,
  DatalayerUpdater,
  TMSFetchOptions,
  TrackTMSPageDatalayer,
} from './types';
import { get } from '../../utils/httpRequest';
import {
  DATALAYER_STORAGE_KEY,
  STORAGE_KEY,
  TC_VARS,
} from '../resources/constants';
import { StorageTrackingData } from '../types';
import { handleTmsDataError } from './error';

/**
 * Initializes the inner TC vars.
 * When it's not available, it tries to get it from the opener tab (if available).
 * @returns {Partial<Datalayer>} - The inner TC vars.
 */
const initInnerTcVars = (): Partial<Datalayer> => {
  const innerTcVars = window.tc_vars;
  if (innerTcVars) {
    return innerTcVars;
  }
  try {
    return (
      window?.opener?.tc_vars ||
      window?.opener?.sessionStorage?.getItem(TC_VARS) ||
      {}
    );
  } catch (_error) {
    // Avoid CORS error
    return {};
  }
};

const getTCVars = (): Partial<Datalayer> => {
  let tcVars: Partial<Datalayer> = getSessionStorageItem(TC_VARS);
  if (!tcVars || Object.keys(tcVars).length === 0) {
    tcVars = initInnerTcVars();
    setTCVars(tcVars);
  }
  return tcVars;
};

const setTCVars = (tcVars: Partial<Datalayer>) => {
  const prevDatalayer: Partial<Datalayer> = getSessionStorageItem(TC_VARS);
  updateSessionTcVars({ ...prevDatalayer, ...tcVars });
};

/**
 * Creates a datalayer updater object.
 * @param {DatalayerUpdater} parser - The parser function to transform the input item.
 * @returns {Object} - The datalayer updater object.
 * @deprecated In product libraries, use a custom hook from frontend-core
 */
export const createDatalayerUpdater = (parser: DatalayerUpdater) => ({
  /**
   * Updates the datalayer with the provided item.
   * @param {unknown} item - The item to update the datalayer with.
   */
  update(item) {
    const datalayer: Partial<Datalayer> = {
      ...parser(item),
    };
    updateSessionDatalayer(datalayer);
  },
});

const getStorageClient = () => {
  const { getTrackingStorageClient } = createConfigParametersRepository();
  return getTrackingStorageClient();
};

const getSessionStorageItem = (key: string): unknown => {
  const { getFromStorage } = getStorageClient();
  return getFromStorage('sessionStorage', key, {});
};

const updateSessionDatalayer = (datalayer: Partial<Datalayer>) => {
  const { updateDatalayer } = getStorageClient();
  updateDatalayer(datalayer);
};

/**
 * removes the keys that don't need to be stored in the sessionStorage datalayer
 * @param datalayer
 */
const updateSessionFilteredDatalayer = (datalayer: Partial<Datalayer>) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { env_template, page_name, ...rest } = datalayer;
  if (Object.keys(rest).length) {
    updateSessionDatalayer(rest);
  }
};

const setSessionDatalayer = (datalayer: Partial<Datalayer>) => {
  const { setDatalayer } = getStorageClient();
  setDatalayer(datalayer);
};

const updateSessionTcVars = (datalayer: Partial<Datalayer>) => {
  const { setInStorage } = getStorageClient();
  setInStorage('sessionStorage', TC_VARS, datalayer);
};

/**
 * Retrieves the current datalayer.
 * @returns {Partial<Datalayer>|undefined} - The current datalayer or undefined if not available.
 */
export const getDatalayer = (): Partial<Datalayer> | undefined => {
  const storageTrackingData: StorageTrackingData =
    getSessionStorageItem(STORAGE_KEY);
  return storageTrackingData?.[DATALAYER_STORAGE_KEY];
};

/**
 * Fires a datalayer event with the provided data payload.
 * @param {DataLayerEventPayload} data - The data payload for the event.
 * @deprecated In product libraries, use a custom hook from frontend-core
 */
export const fireDataLayerEvent = (data: DataLayerEventPayload): void => {
  if (!shouldSkipTracking()) {
    window.tC?.event?.click?.(this, data);
  }
};

/**
 * Check if a variable is defined as global so it should be kept when
 * the product or the search changes
 * @param name
 * @returns {boolean}
 */
export const isTMSVariableGlobal = (name: string): boolean => {
  return datalayerGlobal.includes(name);
};

/**
 * Check if the funnel has changed. Or the user started a new search.
 * @param prevDatalayer
 * @param tcVars
 * @returns {boolean}
 */
const funnelChanged = (
  prevDatalayer: Partial<Datalayer>,
  tcVars: Partial<Datalayer>
) => {
  const {
    in_searchID: inSearchID,
    accommodationFunnel_search_id: hsaSearchID,
    env_brand: envBrand,
    product,
  } = tcVars;
  return (
    !!envBrand && // prevent empty datalayer
    (prevDatalayer.product !== product ||
      (inSearchID &&
        inSearchID !== '0' &&
        prevDatalayer.in_searchID !== inSearchID) ||
      (hsaSearchID &&
        prevDatalayer.accommodationFunnel_search_id !== hsaSearchID))
  );
};

const getClientId = (): string => {
  const storageTrackingData: StorageTrackingData =
    getSessionStorageItem(STORAGE_KEY);
  return storageTrackingData?.userProperties?.client_id || 'not-initialized';
};

const getClientSideAttributes = (): Partial<Datalayer> => {
  return {
    ext_ga_client_id: getClientId(),
    page_url: document.location.href,
  };
};

/**
 * Syncs the datalayer with the TC variables.
 * It will keep the previous page name if the funnel has not changed.
 * It will keep the previous value if the variable is not global and the funnel has changed.
 * It will add client side attributes to the datalayer.
 * It will update the window.tc_vars with the new datalayer.
 * It will update the datalayer in the storage.
 */
export function syncTcVars(tcVars: Partial<Datalayer> = {}) {
  const prevDatalayer = getTCVars();
  if (prevDatalayer.page_name !== tcVars.page_name) {
    tcVars.page_previous = prevDatalayer.page_name;
  }
  let storedDatalayer = getDatalayer();
  const hasFunnelChanged =
    funnelChanged(prevDatalayer, {
      ...tcVars,
      ...storedDatalayer,
    }) || funnelChanged(prevDatalayer, tcVars);
  if (hasFunnelChanged) {
    storedDatalayer = cleanUpDatalayer(storedDatalayer, tcVars);
  }
  const syncFn = (key, oldValue, newValue) =>
    newValue || (!hasFunnelChanged || isTMSVariableGlobal(key) ? oldValue : '');
  Object.keys(prevDatalayer).forEach((key) => {
    tcVars[key] = syncFn(key, prevDatalayer[key], tcVars[key]);
  });
  window.tc_vars = {
    ...tcVars,
    ...getClientSideAttributes(),
    ...storedDatalayer,
  };
  updateSessionTcVars(window.tc_vars);
}

/**
 * Skip tracking when monitoring cookie exists.
 * @returns {boolean} - True if the tracking should be skipped, false otherwise.
 */
const shouldSkip = () => {
  return shouldSkipTracking();
};

/**
 * Syncs the datalayer and reloads the tagCommander container.
 * @returns {void}
 */
export const loadContainer = (tcVars: Partial<Datalayer> = {}): void => {
  const { page_previous } = getTCVars();
  const { page_name } = tcVars;
  if (page_name === page_previous || shouldSkip()) {
    return;
  }
  syncTcVars(tcVars);
  window?.tC?.container?.reload?.({
    events: { page: [{}, {}] },
  });
};

/**
 * Trigger a TMS page view event.
 * @param data
 * @param skipFetch
 */
export const trackTMSPage = async (
  data: TrackTMSPageDatalayer,
  { skipFetch }: TMSFetchOptions = {}
): Promise<void> => {
  handleTmsDataError(data);
  updateSessionFilteredDatalayer(data);
  let tcVars: Partial<Datalayer> = { ...data };
  if (!skipFetch) {
    const baseInfoDatalayer = await fetchDatalayer();
    tcVars = { ...baseInfoDatalayer, ...data };
  }
  loadContainer(tcVars);
};

/**
 * Fetches the datalayer from the backend and updates the TC vars.
 * A datalayer contains only basic/general information is needed for the TMS.
 * @returns {Promise<void>}
 */
export const fetchDatalayer = async () => {
  try {
    const response: Datalayer = await get(
      '/travel/service/tms/datalayer/general?product='
    );
    return flatten(response);
  } catch (_) {
    // avoid fetch error
    return {};
  }
};

/**
 * Flattens the datalayer object.
 * @param obj - The datalayer object.
 * @returns {Datalayer} - The flattened datalayer object.
 */
const flatten = (obj: Datalayer) => {
  return Object.keys(obj).reduce((acc, key) => {
    const value = obj[key];
    if (typeof value === 'object' && value !== null) {
      return { ...acc, ...flatten(value) };
    }
    return {
      ...acc,
      [key]:
        value === null || value === undefined || Number.isNaN(value)
          ? ''
          : `${value}`,
    };
  }, {});
};

/**
 * Cleans up the stored datalayer by removing non-global variables.
 * @param datalayer Partial<Datalayer>
 * @param current
 * @returns Partial<Datalayer>
 */
export const cleanUpDatalayer = (
  datalayer: Partial<Datalayer> = {},
  current: Partial<Datalayer> = {}
): Partial<Datalayer> => {
  if (current.product && current.product === datalayer.product) {
    return datalayer;
  }
  const cleanedDatalayer = Object.keys(datalayer).reduce((acc, key) => {
    if (isTMSVariableGlobal(key)) {
      acc[key] = datalayer[key];
    }
    return acc;
  }, {});
  setSessionDatalayer(cleanedDatalayer);
  return cleanedDatalayer;
};
